import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import {
  formatActualTarget,
  muscleKey,
  weeklyTargetDegradedMax,
  weeklyTargetRowLabel,
  type CatalogMuscleSlug,
  type UnitsPreference,
  type WeeklyTargetRow,
} from '@jackedlogs/shared';
import {
  Icon,
  NO_COVERAGE_GLYPH,
  noCoverageWord,
  TargetMeterRow,
  useDisclosure,
  ZONE_GLYPH,
  zoneTextStyle,
  zoneWord,
} from '../index';

import type { useTheme } from './theme';

// AP-3b — the in-workout contextual meter. A collapsed-by-default strip at the
// top of the FocusPanel: the weekly target for the muscles the active exercise
// trains, with THIS session's just-logged sets already folded in (the fold is
// done upstream by useInWorkoutTargets → computeInWorkoutTargets). Same target-
// vs-actual grammar as the Home three-zone card, because it renders the same
// lifted <TargetMeterRow>/<TargetBar> — the two meters can never diverge.
//
// Collapsed: an eyebrow + a one-line glance of the primary muscle ("Chest 8/12
// ✓ on target") so the athlete reads their standing without a tap. Expanded:
// one row per contextual muscle/exercise. Empty rows → the host renders nothing.

type Props = {
  rows: WeeklyTargetRow[];
  units: UnitsPreference;
  locale: string;
  // Resolve an exercise slug → display name for exercise-axis rows. Muscle rows
  // resolve via muscleKey() here.
  resolveExerciseName: (slug: string) => string;
  theme: ReturnType<typeof useTheme>;
};

export function FocusPanelTargetStrip({
  rows,
  units,
  locale,
  resolveExerciseName,
  theme,
}: Props) {
  const { t } = useTranslation();
  const { expanded, toggle, a11yProps } = useDisclosure();
  const styles = makeStyles(theme);

  if (rows.length === 0) return null;

  // Both of these were hand-rolled here, in a file that already imported
  // formatActualTarget from the same module — so the strip was resolving the
  // row label and the neutral-bar scale a second way from Home's card and the
  // coach's panel. The shared helpers exist precisely so the three can't
  // drift; the only local part is WHICH muscle-name resolver to hand in, and
  // this surface's is i18n (t + muscleKey).
  const labelFor = (row: WeeklyTargetRow): string =>
    weeklyTargetRowLabel(row, resolveExerciseName, (slug) =>
      t(muscleKey(slug as CatalogMuscleSlug)),
    );

  // Untargeted (neutral) rows share the busiest row's scale, matching the card.
  const degradedMax = weeklyTargetDegradedMax(rows);

  // Collapsed glance = the first contextual row (the primary muscle), spelled
  // out with its zone so it reads at a glance, plus "+N" for the rest.
  const lead = rows[0]!;
  const { actual: leadActual, target: leadTarget } = formatActualTarget(
    lead,
    units,
    locale,
  );
  const more = rows.length - 1;

  const summaryA11y = [
    labelFor(lead),
    leadTarget != null ? `${leadActual} / ${leadTarget}` : leadActual,
    lead.noCoverage
      ? noCoverageWord(t)
      : lead.zone != null
        ? zoneWord(t, lead.zone)
        : null,
    more > 0
      ? t('mobile.logger.targetsMore', {
          count: more,
          defaultValue: '+{{count}} more',
        })
      : null,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <View style={styles.box}>
      <Pressable
        {...a11yProps}
        accessibilityRole="button"
        accessibilityLabel={`${t('mobile.logger.targetEyebrow', { defaultValue: 'Target' })}. ${summaryA11y}`}
        onPress={toggle}
        style={({ pressed }) => [styles.header, pressed && styles.pressed]}
      >
        {/* Eyebrow + glance STACK vertically (matching the Home card's headText
            column) so a long eyebrow ("Questa settimana" / "На этой неделе")
            never fights the value on one row and crowds the chevron. */}
        <View style={styles.headText}>
          <Text style={styles.eyebrow} numberOfLines={1}>
            {t('mobile.logger.targetEyebrow', { defaultValue: 'Target' })}
          </Text>
          {!expanded ? (
            <View style={styles.summary}>
              <Text style={styles.summaryLabel} numberOfLines={1}>
                {labelFor(lead)}
              </Text>
              <Text style={styles.summaryValue}>
                {leadActual}
                {leadTarget != null ? (
                  <Text style={styles.summaryTarget}>
                    {' / '}
                    {leadTarget}
                  </Text>
                ) : null}
              </Text>
              {lead.noCoverage ? (
                <Text style={[styles.summaryZone, styles.summaryNoCoverage]}>
                  {NO_COVERAGE_GLYPH}
                </Text>
              ) : lead.zone != null ? (
                <Text style={[styles.summaryZone, zoneTextStyle(theme, lead.zone)]}>
                  {ZONE_GLYPH[lead.zone]}
                </Text>
              ) : null}
              {more > 0 ? (
                <Text style={styles.summaryMore}>{`+${more}`}</Text>
              ) : null}
            </View>
          ) : null}
        </View>
        <Icon
          name={expanded ? 'chevron-up' : 'chevron-down'}
          size={theme.typography.sizes.md}
          color={theme.palette.ink3}
        />
      </Pressable>

      {expanded ? (
        <View style={styles.body}>
          {rows.map((row) => (
            <View key={`${row.axis}:${row.key}:${row.metric}`} style={styles.row}>
              <TargetMeterRow
                theme={theme}
                row={row}
                label={labelFor(row)}
                units={units}
                locale={locale}
                degradedMax={degradedMax}
              />
            </View>
          ))}
        </View>
      ) : null}
    </View>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, typography: ty, radius: r } = theme;
  return StyleSheet.create({
    // Same surface + r3 as the Home meter card (glanceCardStyles.makeHomeBoxStyle
    // uses surface/r3) — surface, not surface2, keeps the zone `complete` text
    // at the card's contrast (surface2 dropped it further, DS-13 watch).
    box: {
      backgroundColor: p.surface,
      borderRadius: r.r3,
      borderWidth: 1,
      borderColor: p.border,
      marginBottom: s.s3,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s3,
      paddingHorizontal: s.s3,
      paddingVertical: s.s2,
      minHeight: 44,
    },
    pressed: { opacity: 0.7 },
    // Eyebrow stacked above the glance, matching the Home card's headText.
    headText: { flex: 1, gap: 2 },
    eyebrow: {
      color: p.ink3,
      fontSize: ty.sizes.sm,
      fontWeight: '700',
      letterSpacing: 0.4,
    },
    summary: {
      flexDirection: 'row',
      alignItems: 'baseline',
      flexWrap: 'wrap',
      gap: s.s2,
    },
    // md/600 to match the Home card's title weight; the number reads at md so
    // it stays legible mid-set (was sm — the a11y audit's step-down finding).
    summaryLabel: {
      color: p.ink,
      fontSize: ty.sizes.md,
      fontWeight: '600',
      flexShrink: 1,
    },
    summaryValue: {
      color: p.ink,
      fontFamily: ty.fonts.mono,
      fontSize: ty.sizes.md,
      fontWeight: '700',
      fontVariant: ['tabular-nums'],
    },
    summaryTarget: { color: p.ink3, fontWeight: '600' },
    summaryZone: { fontSize: ty.sizes.md, fontWeight: '700' },
    // "no coverage" glyph reads at ink2 — louder than a zone glyph so the
    // collapsed glance, which shows the mark without its word, still catches the
    // eye; the ∅ silhouette (not colour) is what sets it apart from a zone glyph.
    summaryNoCoverage: { color: p.ink2 },
    summaryMore: { color: p.ink3, fontSize: ty.sizes.sm, fontWeight: '600' },
    body: {
      borderTopWidth: 1,
      borderTopColor: p.border,
      paddingHorizontal: s.s3,
      paddingBottom: s.s2,
    },
    row: {
      paddingTop: s.s2,
      paddingBottom: s.s1,
    },
  });
}
